import TemplateView from '../../../TemplateView'
import PasswordErrorMessage from '../../../util/passwords/PasswordErrorMessage'
import {getPasswordValidator} from '../../../util/passwords/PasswordAsyncValidator'
import {ValidationErrors} from '../../../panels/user/constants'
import messageResource from '../../../messageResource'

export default class ResetPasswordsDialog extends TemplateView {
    constructor({
        okButtonClick = () => {},
        cancelButtonClick = () => {},
        updateCurrentPasswordCb = () => {},
        updateNewPasswordCb = () => {},
        updateNewPasswordInvalidCb = () => {},
    } = {}) {
        super()

        this.state = {
            needToSetNewPassword: false,
            newPasswordPolicy: {},
            currentPasswordInvalid: false,
            newPasswordInvalid: false,
        }

        this.actions = {
            okButtonClick,
            cancelButtonClick,
        }

        this.callbacks = {
            updateCurrentPasswordCb,
            updateNewPasswordCb,
            updateNewPasswordInvalidCb,
        }

        this.onStatePropertyChanged("needToSetNewPassword", needToSetNewPassword => {
            this.$confirmPasswordBlock.toggle(!needToSetNewPassword)
            this.$resetPasswordBlock.toggle(needToSetNewPassword)

        })

        this.onAnyStatePropertyChanged(['needToSetNewPassword', 'currentPasswordInvalid', 'newPasswordInvalid'], () => {
            this.toggleResetButton()
        })

        this.onStatePropertyChanged("currentPasswordInvalid", currentPasswordInvalid => {
            this.$currentPasswordError.toggle(currentPasswordInvalid)
            this.$currentPassword.toggleClass('invalid', currentPasswordInvalid)
            this.$confirmPassword.toggleClass('invalid', currentPasswordInvalid)
        })

        this.onStatePropertyChanged("newPasswordInvalid", newPasswordInvalid => {
            this.$newPassword.toggleClass('invalid', newPasswordInvalid)
            this.$retypeNewPassword.toggleClass('invalid', newPasswordInvalid)
        })

        this.onStatePropertyChanged("newPasswordPolicy", newPasswordPolicy => {
            this.passwordAsyncValidator.setState({
                newPasswordPolicy
            })
        })
    }

    templateHtml() {
        return '<div class="generalSettings_resetPasswordsDialog">' +
               '    <div class="titleBlock">' +
               '        <div class="title"><%=title%></div>' +
               '        <div class="note"><%=titleNote%></div>' +
               '    </div>' +
               '    <div class="content">' +
               '        <div class="confirmPasswordBlock">' +
               '            <div class="note"><%=confirmNote%></div>' +
               '            <input class="password" type="password" maxlength="255" placeholder="<%=currentPassword%>"/>' +
               '        </div>' +
               '        <div class="resetPasswordBlock">' +
               '            <div class="note"><%=resetNote%></div>' +
               '            <div class="inputsContainer">' +
               '                <input class="currentPassword" type="password" maxlength="255" placeholder="<%=currentPassword%>"/>' +
               '                <input class="newPassword" type="password" maxlength="255" placeholder="<%=newPassword%>"/>' +
               '                <input class="retypeNewPassword" type="password" maxlength="255" placeholder="<%=retypeNewPassword%>"/>' +
               '            </div>' +
               '            <div class="passwordPolicyError"></div>' +
               '        </div>' +
               '        <div class="currentPasswordError"><%=currentPasswordInvalid%></div>' +
               '    </div>' +
               '    <div class="generalSettings_resetPasswordsDialog_buttons">' +
               '        <button class="button ok"><%=okButtonText%></button>' +
               '        <button class="button cancel"><%=cancelButtonText%></button>' +
               '    </div>' +
               '</div>'
    }

    messageKeys() {
        return {
            title: 'password_settings.reset_confirmation.title',
            titleNote: 'password_settings.reset_confirmation.text',
            cancelButtonText: 'password_settings.reset_confirmation.cancel_button',
            okButtonText: 'password_settings.reset_confirmation.reset_button',
            confirmNote: 'password_settings.reset_confirmation.confirm_note',
            resetNote: 'password_settings.reset_confirmation.reset_note',
            currentPassword: 'password_settings.reset_confirmation.current_password_placeholder',
            newPassword: 'password_settings.reset_confirmation.new_password_placeholder',
            retypeNewPassword: 'password_settings.reset_confirmation.retype_new_password_placeholder',
            currentPasswordInvalid: 'password_settings.reset.passwords_invalid',
        }
    }

    afterRender() {
        this.$confirmPasswordBlock = this.find('.confirmPasswordBlock')
        this.$resetPasswordBlock = this.find('.resetPasswordBlock')
        this.$currentPasswordError = this.find('.currentPasswordError')

        this.okButton = this.find(".button.ok")
        this.okButton.click((e) => {
            e.preventDefault()
            if (!this.isResetDisabled())
                this.actions.okButtonClick(e)
        })

        this.find(".button.cancel").click(this.actions.cancelButtonClick)

        //confirm password
        this.$confirmPassword = this.find('.confirmPasswordBlock .password')
        this.$confirmPassword.keyup(() => {
            this.toggleResetButton()
            this.callbacks.updateCurrentPasswordCb(this.getCurrentPasswordEl().pVal())
        })

        //reset password
        this.$currentPassword = this.find('.resetPasswordBlock .currentPassword')
        this.$currentPassword.keyup(() => {
            this.toggleResetButton()
            this.callbacks.updateCurrentPasswordCb(this.getCurrentPasswordEl().pVal())
        })

        this.$newPassword = this.find('.resetPasswordBlock .newPassword')
        this.$retypeNewPassword = this.find('.resetPasswordBlock .retypeNewPassword')

        this.passwordFieldsError = new PasswordErrorMessage().init(this.find('.passwordPolicyError'))
        this.passwordAsyncValidator = getPasswordValidator({
            ignoreBothEmptyness: true,
            validateWithNewPolicy: true,
            onValidationEnd: (errorKey, passwordValidationResult) => this.showNewPasswordErrorMessage(errorKey, passwordValidationResult)
        })

        this.$newPassword.blur(() => this.validateNewPasswordErrors())
        this.$retypeNewPassword.blur(() => this.validateNewPasswordErrors())

        this.renderInitialState()
    }

    validateNewPasswordErrors() {
        this.passwordAsyncValidator.setState({
            password: this.$newPassword.pVal(),
            passwordCopy: this.$retypeNewPassword.pVal(),
            forcePasswordCheck: true,
        })
    }

    showNewPasswordErrorMessage(errorKey, passwordValidationResult) {
        let hasError = true
        switch (errorKey) {
            case ValidationErrors.PASSWORD_EMPTY:
                this.passwordFieldsError.showError(messageResource.getMessage("password_settings.reset.password_empty"))
                break
            case ValidationErrors.PASSWORD_COPY_EMPTY:
            case ValidationErrors.PASSWORDS_DO_NOT_MATCH:
                this.passwordFieldsError.showError(messageResource.getMessage("password_settings.reset.passwords_do_not_match"))
                break
            case ValidationErrors.PASSWORD_RESTRICTIONS_VIOLATED:
                this.passwordFieldsError.setState({passwordValidationResult})
                break
            case null:
                hasError = false
                this.passwordFieldsError.hideError()
                this.callbacks.updateNewPasswordCb(this.$newPassword.pVal())
                break
            default:
                console.error("Unprocessed error code:", errorKey)
        }

        this.callbacks.updateNewPasswordInvalidCb(hasError)
    }

    updatePolicyViolatedError(passwordValidationResult) {
        this.passwordFieldsError.setState({passwordValidationResult})
    }

    getCurrentPasswordEl() {
        if (!this.state.needToSetNewPassword)
            return this.$confirmPassword
        else return this.$currentPassword
    }

    clearFields() {
        this.$confirmPassword.pVal('')
        this.$currentPassword.pVal('')
        this.$newPassword.pVal('')
        this.$retypeNewPassword.pVal('')
        this.passwordFieldsError.hideError()
        this.toggleResetButton()
    }

    toggleResetButton() {
        const resetDisabled = this.isResetDisabled()
        this.okButton.toggleClass('disabled', resetDisabled)
    }

    isResetDisabled() {
        if (!this.state.needToSetNewPassword) {
            return this.state.currentPasswordInvalid || this.$confirmPassword.pVal().length === 0
        } else {
            return this.state.currentPasswordInvalid || this.$currentPassword.pVal().length === 0
                   || this.state.newPasswordInvalid || this.$newPassword.pVal().length === 0
        }
    }
}



// WEBPACK FOOTER //
// ./web/js/administration/passwordSettings/components/ResetPasswordsDialog.js